import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import IconButton from "@mui/material/IconButton";
import SettingsIcon from "@mui/icons-material/Settings";
import Dialog from "@mui/material/Dialog";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

// Data
import projectsTableData from "layouts/tables/data/projectsTableData";
import React, { useEffect, useState, useCallback } from "react";
import ChatbotLogin from "layouts/chatbot/components/ChatbotLogin";
import { useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useAuth } from "context/auth.context";
import appConfig from "../../config";

// Hook na načítanie chatbotov z API
function useChatbotsTableData(onConfigClick, refreshKey) {
  const [data, setData] = useState({ columns: [], rows: [] });
  const { user, loading } = useAuth();

  useEffect(() => {
    if (loading || !user) {
      setData({ columns: [], rows: [] });
      return;
    }
    fetch(`${appConfig.BASE_URL}/api/chatbots`, {
      credentials: "include",
    })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch chatbots");
        return res.json();
      })
      .then((chatbots) => {
        setData({
          columns: [
            { Header: "ID", accessor: "id", align: "left" },
            { Header: "Name", accessor: "name", align: "left" },
            { Header: "User ID", accessor: "user_id", align: "left" },
            { Header: "Token", accessor: "token", align: "left" },
            { Header: "", accessor: "action", align: "center" },
          ],
          rows: chatbots.map((bot) => ({
            id: bot.id,
            name: bot.name,
            user_id: bot.user_id,
            token: bot.token,
            action: (
              <IconButton onClick={() => onConfigClick(bot)} color="info">
                <SettingsIcon />
              </IconButton>
            ),
          })),
        });
      })
      .catch(() => {
        setData({ columns: [], rows: [] });
      });
  }, [user, loading, refreshKey, onConfigClick]);

  return data;
}

function Tables() {
  const navigate = useNavigate();
  const [loginOpen, setLoginOpen] = useState(false);
  const [selectedBot, setSelectedBot] = useState(null);
  const [loginLoadingId, setLoginLoadingId] = useState(null);
  const [loginErrorId, setLoginErrorId] = useState(null);
  const [loggedInToken, setLoggedInToken] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const handleConfigClick = useCallback(
    async (bot) => {
      setLoginLoadingId(bot.id);
      setLoginErrorId(null);
      try {
        const res = await fetch(`${appConfig.BASE_URL}/v1/chat/completions`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${bot.token}`,
          },
          body: JSON.stringify({
            model: "llama-3.1-8b-instant",
            messages: [],
            max_completion_tokens: 1,
          }),
        });
        let data = null;
        try {
          data = await res.json();
        } catch {}
        if (!res.ok) {
          if (res.status === 401) {
            setLoginErrorId(bot.id);
          } else if (data && data.detail) {
            setLoginErrorId(bot.id);
          } else {
            setLoginErrorId(bot.id);
          }
          setLoginLoadingId(null);
          return;
        }
        setLoggedInToken(bot.token);
        setLoginLoadingId(null);
        setLoginErrorId(null);
        navigate("/chatbot-configuration", { state: { token: bot.token } });
      } catch (err) {
        setLoginErrorId(bot.id);
        setLoginLoadingId(null);
      }
    },
    [navigate]
  );

  const handleRefresh = () => setRefreshKey((k) => k + 1);
  const handleCreate = () => navigate("/chatbot-configuration", { state: { showRegister: true } });

  const { columns, rows } = useChatbotsTableData(handleConfigClick, refreshKey);
  const { columns: pColumns, rows: pRows } = projectsTableData();

  // Pridám loading a error stav do action stĺpca
  const enhancedRows = rows.map((row) => ({
    ...row,
    action: (
      <>
        <IconButton
          onClick={() => handleConfigClick(row)}
          color="info"
          disabled={loginLoadingId === row.id}
        >
          <SettingsIcon />
        </IconButton>
        {loginLoadingId === row.id && (
          <MDTypography variant="caption" color="info" ml={1}>
            Overujem...
          </MDTypography>
        )}
        {loginErrorId === row.id && (
          <MDTypography variant="caption" color="error" ml={1}>
            Neplatný token alebo chyba
          </MDTypography>
        )}
      </>
    ),
  }));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <MDTypography variant="h6" color="text">
                Chatbots Table
              </MDTypography>
              <MDBox display="flex" gap={1}>
                <MDButton
                  onClick={handleRefresh}
                  color="info"
                  variant="outlined"
                  size="small"
                  startIcon={<RefreshIcon />}
                >
                  Refresh
                </MDButton>
                <MDButton
                  onClick={handleCreate}
                  color="info"
                  variant="gradient"
                  size="small"
                  startIcon={<AddIcon />}
                >
                  Nový
                </MDButton>
              </MDBox>
            </MDBox>
            <Card>
              <MDBox pt={3}>
                <DataTable
                  table={{ columns, rows: enhancedRows }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Dialog open={loginOpen} onClose={() => setLoginOpen(false)} maxWidth="xs" fullWidth>
        {selectedBot && <ChatbotLogin setToken={handleLoginSuccess} onShowRegister={() => {}} />}
      </Dialog>
      <Footer />
    </DashboardLayout>
  );
}

export default Tables;
